import React, { useEffect, useMemo, useState } from 'react';
import {
  Heading, Icon, Listbox, Grid, CheckboxGroup, LinkAsButton, Spinner,
} from '@printdeal/ui-library';
import { FormattedMessage, useIntl } from 'react-intl';
import { useInView } from 'react-intersection-observer';
import { useQuery } from '@tanstack/react-query';
import { dispatchDataLayerEntry } from '../../../../helpers/dataLayerHelpers/productDataLayerHelper';
import { ReviewStars } from '../../../review-stars/ReviewStars';
import {
  fetchProductReviews, ProductReviews, Review, Sort,
} from '../../../../helpers/reviewHelper';
import { Locale } from '../../../../types/gatsby';
import { ReviewReplies } from './ReviewReplies';
import { TimeAgo } from './TimeAgo';
import UserImagesGallery from './UserImagesGallery';

interface ReviewIOProps {
  category?: string;
  productName?: string;
  contentfulId: string;
  productReviews: ProductReviews;
}

const ReviewsIO = ({
  category, productName, contentfulId, productReviews,
}: ReviewIOProps) => {
  const intl = useIntl();
  const locale = intl.locale as Locale;
  const [reviews, setReviews] = useState<Review[]>(productReviews.reviews);
  const [resultCount, setResultCount] = useState<number>(productReviews.results_count);
  const [page, setPage] = useState<number | undefined>();
  const [stars, setStars] = useState<number[]>([]);
  const [sort, setSort] = useState<Sort | undefined>();
  const userImages = productReviews.reviewsWithImages.flatMap((review: Review): string [] => review.photos || []);

  const sortedRatings = useMemo(() =>
    Object.keys(productReviews.ratings)
      .sort((ratingA, ratingB) => Number(ratingB) - Number(ratingA)),
  [productReviews.ratings]);

  const { data, fetchStatus } = useQuery({
    queryKey: ['productReviews', { page, stars, sort }],
    queryFn: () => fetchProductReviews({
      contentfulId, locale, page, stars, sort,
    }),
    enabled: (page !== undefined || stars.length > 0 || sort !== undefined),
  });

  useEffect(() => {
    if (data) {
      setResultCount(data.results_count);
      setReviews((prev) => (page === 1 ? data.reviews : [...prev, ...data.reviews]));
    }
  }, [data, page]);

  const handleSort = (value: Sort) => {
    setPage(1);
    setSort(value);
  };

  const handleStarRating = (star: number) => {
    setStars((prev) => {
      if (prev.includes(star)) {
        return prev.filter((item) => item !== star);
      }
      return [...prev, star];
    });
    setPage(1);
  };

  const fetchMoreReviews = () => {
    // We start at page 2 because we already have the first page of reviews
    setPage((prev) => (prev ? prev + 1 : 2));
  };

  const { ref } = useInView({
    triggerOnce: true,
    onChange: (inView) => inView && dispatchDataLayerEntry('productDetailReviews', 'display'),
  });

  const customerImagesTitle = intl.formatMessage({
    id: 'productDetail.reviews.customerImages', defaultMessage: 'Customer images',
  });

  return (
    <Grid className="pd-grid pd-grid-cols-1 pd-gap-6">
      <Grid.Item>
        <Heading as="h2" variant="display">
          <FormattedMessage id="productDetail.reviews.title" defaultMessage="Reviews" />
        </Heading>
      </Grid.Item>
      <Grid.Item>
        <div className="pd-flex pd-items-center pd-gap-2">
          <ReviewStars score={productReviews.average_rating} />
          <FormattedMessage
            id="productDetail.reviews.total"
            defaultMessage="{total} Reviews"
            values={{ total: productReviews.results_count }}
          />
        </div>
      </Grid.Item>
      <Grid.Item>
        <CheckboxGroup id="star-filter" name="star-filter">
          {sortedRatings.map((rating) => (
            <CheckboxGroup.Field
              key={rating}
              variant="secondary"
              id={rating}
              label={(
                <FormattedMessage
                  id="productDetail.reviews.stars"
                  defaultMessage="{rating} {rating, plural, one {star} other {stars}} ({count})"
                  values={{
                    rating,
                    count: productReviews.ratings[rating],
                  }}
                />
                )}
              onChange={() => handleStarRating(Number(rating))}
            />
          ))}
        </CheckboxGroup>
      </Grid.Item>
      {userImages.length > 0 && (
        <>
          <Grid.Item>
            <Heading as="h3" variant="display">
              {customerImagesTitle}
            </Heading>
          </Grid.Item>
          <Grid.Item>
            <UserImagesGallery
              category={category}
              productName={productName}
              title={customerImagesTitle}
              crawlerId="user-images"
              userImages={userImages}
              reviews={productReviews.reviewsWithImages}
            />
          </Grid.Item>
        </>
      )}
      <Grid.Item>
        <div className="pd-flex pd-justify-start">
          <Listbox value={sort || 'date_desc'} onChange={(value) => handleSort(value as Sort)}>
            <Listbox.Option value="date_desc">
              <FormattedMessage id="productDetail.reviews.filter.newest" defaultMessage="Newest reviews" />
            </Listbox.Option>
            <Listbox.Option value="date_asc">
              <FormattedMessage id="productDetail.reviews.filter.oldest" defaultMessage="Oldest reviews" />
            </Listbox.Option>
            <Listbox.Option value="highest_rating">
              <FormattedMessage id="productDetail.reviews.filter.highest" defaultMessage="Highest reviews" />
            </Listbox.Option>
            <Listbox.Option value="lowest_rating">
              <FormattedMessage id="productDetail.reviews.filter.lowest" defaultMessage="Lowest reviews" />
            </Listbox.Option>
          </Listbox>
        </div>
      </Grid.Item>
      {reviews.map((review) => (
        <Grid.Item key={review.id}>
          <Grid ref={ref} className="pd-grid pd-grid-cols-1 pd-gap-3">
            {review.title && (
              <Grid.Item className="pd-text-md pd-font-bold">{review.title}</Grid.Item>
            )}
            <Grid.Item className="pd-flex pd-items-center">
              <ReviewStars score={review.rating * 2} className="pd-mr-2" />
              {review.rating}/5
            </Grid.Item>
            <Grid.Item className="pd-flex pd-items-center">
              <Icon icon="check" className="pd-mr-2 pd-text-success-600" size="md" />
              <FormattedMessage id="productDetail.reviews.verified" defaultMessage="Verified customer" />
            </Grid.Item>
            <Grid.Item>
              {review.author.name}
              {review.author.location && ` / ${review.author.location}`}{' '}
              <TimeAgo date={review.date_created} />
            </Grid.Item>
            <Grid.Item>
              <p className="pd-whitespace-pre-wrap">{review.comments}</p>
            </Grid.Item>
            {review.photos && review.photos.length > 0 && (
              <Grid.Item>
                <UserImagesGallery
                  category={category}
                  productName={productName}
                  title={review.author.name}
                  crawlerId={`user-images-${review.id}`}
                  userImages={review.photos}
                  singleReview={review}
                />
              </Grid.Item>
            )}
            {review.replies.length > 0 && (
              <ReviewReplies replies={review.replies} contentfulId={contentfulId} />
            )}
          </Grid>
        </Grid.Item>
      ))}
      {resultCount > reviews.length && fetchStatus !== 'fetching' && (
        <Grid.Item>
          <LinkAsButton
            type="button"
            onClick={fetchMoreReviews}
            className="pd-pl-1 pd-text-md pd-font-semibold pd-text-black"
            iconBefore={(<Icon icon="double-chevron-down" className="pd-text-dim-500" />)}
          >
            <FormattedMessage id="productDetail.reviews.more" defaultMessage="Show more reviews" />
          </LinkAsButton>
        </Grid.Item>
      )}
      {fetchStatus === 'fetching' && (
        <Grid.Item>
          <div className="pd-flex pd-items-center pd-justify-start pd-gap-2">
            <Spinner variant="primary" screenReaderLabel={intl.formatMessage({ id: 'uilib.spinner.loading', defaultMessage: 'Loading...' })} />
            <FormattedMessage id="productDetail.reviews.retrieving" defaultMessage="Retrieving reviews..." />
          </div>
        </Grid.Item>
      )}
    </Grid>
  );
};

export default ReviewsIO;
